import LoginForm from '@gaia/components/LoginForm';
import RegistrationForm from '@gaia/components/RegistrationForm';
import {useLatest} from '@gaia/hooks/useLatest';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import {Dialog, DialogContent, Slide} from '@mui/material';
import {useEffect} from 'react';

import AuthSuccess from './AuthSuccess';

export type AuthModalType = 'login' | 'register';

export interface AuthModalState {
    type: AuthModalType | undefined;
    onUserAuthenticated?: (type?: AuthModalType) => void;
    analyticsContext: string | undefined;
}

interface AuthModalProps extends AuthModalState {
    setAuthModalType: (type: AuthModalType | undefined) => void;
}

export default function AuthModal({
    type,
    onUserAuthenticated,
    analyticsContext,
    setAuthModalType,
}: AuthModalProps) {
    const {isAuthenticated} = useUser();

    const latest = useLatest({type, onUserAuthenticated, analyticsContext, setAuthModalType});

    useEffect(() => {
        if (isAuthenticated) {
            setTimeout(() => {
                latest.current.setAuthModalType(undefined);
                latest.current.onUserAuthenticated?.(latest.current.type);
            }, 1500);
        }
    }, [isAuthenticated, latest]);

    return (
        <Dialog
            open={type !== undefined}
            onClose={() => setAuthModalType(undefined)}
            maxWidth='md'
            // Make sure this draws above all other components
            style={{zIndex: 1501}}
        >
            <DialogContent
                style={{
                    padding: theme.spacing(4),
                    boxSizing: 'border-box',
                }}
            >
                <Slide direction='left' in={type === 'register'} unmountOnExit timeout={450}>
                    <div
                        style={{
                            // Must be absolute for smooth slide transition between login and registration forms
                            position: type !== 'register' ? 'absolute' : undefined,
                        }}
                    >
                        {isAuthenticated ? (
                            <AuthSuccess in={isAuthenticated} message={'Thanks for joining!'} />
                        ) : (
                            <RegistrationForm
                                nextPage={window.location.pathname}
                                setAuthModalType={setAuthModalType}
                                analyticsContext={analyticsContext}
                            />
                        )}
                    </div>
                </Slide>

                <Slide direction='right' in={type === 'login'} unmountOnExit timeout={450}>
                    <div
                        style={{
                            // Must be absolute for smooth slide transition between login and registration forms
                            position: type !== 'login' ? 'absolute' : undefined,
                        }}
                    >
                        {isAuthenticated ? (
                            <AuthSuccess in={isAuthenticated} message={'Welcome back!'} />
                        ) : (
                            <LoginForm
                                nextPage={window.location.pathname}
                                setAuthModalType={setAuthModalType}
                                analyticsContext={analyticsContext}
                            />
                        )}
                    </div>
                </Slide>
            </DialogContent>
        </Dialog>
    );
}
